<template>
  <Transition name="network-error-banner">
    <div v-if="visible" class="network-error-banner" role="alert">
      <span class="network-error-banner__text">
        <TipTapRenderer v-if="message" :content="message" />
        <template v-else>{{ FALLBACK_MESSAGE }}</template>
      </span>
      <button
        type="button"
        class="network-error-banner__close"
        aria-label="Close"
        @click="visible = false"
      >
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { PIPE_EVENTS, usePipe } from '@render/composables/usePipe'
import TipTapRenderer from '@render/lib/TipTapRenderer.vue'

// Seeded by the API's TranslationSeeder (global group; other languages are
// authored in the translations admin). Pipes built before the key existed
// fall back to the hardcoded English text — keep the two in sync.
const NETWORK_ERROR_TRANSLATION_KEY = 'global.networkError'
const FALLBACK_MESSAGE =
  'Connection problem. Please check your internet connection and refresh the page.'

const pipe = usePipe()
const visible = ref(false)

const message = computed(() => pipe.getTranslation(NETWORK_ERROR_TRANSLATION_KEY))

// Self-dismiss on page change: the built pipe fires `navigation:leave` from
// [...slug].vue's route watcher; the editor preview has no router and only
// emits `navigation:show` on a schema swap (PreviewApp UPDATE_RENDER), so
// subscribe to both. `navigation:show` replays to late subscribers, but the
// replayed callback just re-asserts the initial hidden state — harmless.
let unsubscribers: Array<() => void> = []
onMounted(() => {
  unsubscribers = [
    pipe.subscribe(PIPE_EVENTS.networkError, () => {
      visible.value = true
    }),
    pipe.subscribe('navigation:leave', () => {
      visible.value = false
    }),
    pipe.subscribe('navigation:show', () => {
      visible.value = false
    }),
  ]
})
onBeforeUnmount(() => {
  unsubscribers.forEach((unsubscribe) => unsubscribe())
  unsubscribers = []
})
</script>

<style scoped lang="scss">
.network-error-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  // Above everything a pipe can render — the highest in-pipe overlays
  // (Confetti / ConfettiFountains) sit at 9999.
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: var(--space-12, 12px);
  padding: var(--space-12, 12px) var(--space-16, 16px);
  // Keep the text out of notches / status bars on standalone mobile.
  padding-top: calc(var(--space-12, 12px) + env(safe-area-inset-top, 0px));
  background-color: var(--color-feedback-error-background, #b91c1c);
  color: var(--color-feedback-error-text-on-bg, #ffffff);
  border-bottom: var(--border-width, 1px) solid var(--color-feedback-error-border, transparent);
  box-shadow: var(--shadow-md, 0 2px 8px rgba(0, 0, 0, 0.2));
  font-family: var(--text-body-md-family, inherit);
  font-size: var(--text-body-md-size, 0.875rem);
  font-weight: var(--text-body-md-weight, 400);
  line-height: var(--text-body-md-line-height, 1.4);

  &__text {
    flex: 1;
    min-width: 0;

    :deep(p) {
      margin: 0;
    }
  }

  &__close {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--space-24, 24px);
    height: var(--space-24, 24px);
    padding: 0;
    border: none;
    border-radius: var(--radius-sm, 4px);
    background: transparent;
    color: inherit;
    cursor: pointer;
  }
}

.network-error-banner-enter-active,
.network-error-banner-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}

.network-error-banner-enter-from,
.network-error-banner-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
